import { useQuery } from '@tanstack/react-query';
import { useEffect, useRef } from 'react';

import { NEW_NAVIGATION } from '@app/constants/feature-definitions';
import { isDetailsNavFrameworkEnabled } from '@app/helpers/featureFlags';
import queryClient from '@app/query-client';
import {
  type EffectiveFeatureMap,
  effectiveFeaturesQuery,
  resolveFeatureEnabled,
} from '@app/services/organization-features';

export const NEW_NAVIGATION_QUERY_KEY = ['newNavigation'];

const LOOKUP_TIMEOUT_MS = 5000;

// Not the shared key: cancelling on timeout must not affect other gates.
const NAVIGATION_FEATURE_MAP_QUERY_KEY = [
  ...NEW_NAVIGATION_QUERY_KEY,
  'featureMap',
];

const fetchFeatureMap = (staleTime = 0): Promise<EffectiveFeatureMap> =>
  new Promise<EffectiveFeatureMap>((resolve, reject) => {
    const timer = setTimeout(() => {
      queryClient.cancelQueries({
        queryKey: NAVIGATION_FEATURE_MAP_QUERY_KEY,
        exact: true,
      });
      reject(new Error('New navigation lookup timed out'));
    }, LOOKUP_TIMEOUT_MS);
    queryClient
      .fetchQuery({
        queryKey: NAVIGATION_FEATURE_MAP_QUERY_KEY,
        queryFn: effectiveFeaturesQuery().queryFn,
        staleTime,
        retry: false,
      })
      .then(
        (map) => {
          clearTimeout(timer);
          resolve(map);
        },
        (error: unknown) => {
          clearTimeout(timer);
          reject(error);
        },
      );
  });

const readCachedFeatureMap = () =>
  queryClient.getQueryData<EffectiveFeatureMap>(
    effectiveFeaturesQuery().queryKey,
  );

export const useNewNavigationGate = (): {
  resolved: boolean;
  enabled: boolean;
} => {
  const ssmEnabled = isDetailsNavFrameworkEnabled();
  const { data, isLoading } = useQuery({
    queryKey: NEW_NAVIGATION_QUERY_KEY,
    queryFn: async () =>
      resolveFeatureEnabled(await fetchFeatureMap(), NEW_NAVIGATION),
    initialData: () => {
      const map = readCachedFeatureMap();
      return map ? resolveFeatureEnabled(map, NEW_NAVIGATION) : undefined;
    },
    enabled: ssmEnabled,
    retry: false,
    staleTime: Infinity,
    cacheTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

  if (!ssmEnabled) return { resolved: true, enabled: false };
  return { resolved: !isLoading, enabled: data ?? true };
};

export const useIsNewNavigationEnabled = (): boolean =>
  useNewNavigationGate().enabled;

const ROUTE_CHANGE_STALE_TIME_MS = 60 * 1000;

export const useApplyNewNavigationOnRouteChange = (pathname: string): void => {
  const lastPathnameRef = useRef(pathname);

  useEffect(() => {
    if (pathname === lastPathnameRef.current) return;
    lastPathnameRef.current = pathname;
    fetchFeatureMap(ROUTE_CHANGE_STALE_TIME_MS)
      .then((map) => {
        const latest = resolveFeatureEnabled(map, NEW_NAVIGATION);
        if (queryClient.getQueryData(NEW_NAVIGATION_QUERY_KEY) !== latest) {
          queryClient.setQueryData(NEW_NAVIGATION_QUERY_KEY, latest);
        }
      })
      .catch(() => undefined);
  }, [pathname]);
};

export const applyNewNavigation = (enabled: boolean): void => {
  queryClient.removeQueries({
    queryKey: NAVIGATION_FEATURE_MAP_QUERY_KEY,
    exact: true,
  });
  queryClient.setQueryData(NEW_NAVIGATION_QUERY_KEY, enabled);
};

export const resetNewNavigation = (): void => {
  queryClient.removeQueries({ queryKey: NEW_NAVIGATION_QUERY_KEY });
  queryClient.removeQueries({ queryKey: effectiveFeaturesQuery().queryKey });
};
